import { useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { CONTENT_MODELS, type ContentModelId } from '@cp/shared';
import { Button, Checkbox, Dialog, Field, Input, PasswordInput, Select } from '@/components/ui';
import { useSaveIntegration } from '@/hooks/useIntegrations';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import { INTEGRATION_NAMES } from '@/utils/integrations';
import type { IntegrationKind, IntegrationView } from '@/types/api';

const MODEL_OPTIONS: { value: ContentModelId; label: string; hint: string }[] = [
  { value: 'claude-opus-5', label: 'Claude Opus 5', hint: 'Лучшее качество · $5 / $25 за 1 млн токенов' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', hint: 'Дешевле в 2,5 раза · $2 / $10 за 1 млн токенов' },
];

const settingsOf = (view: IntegrationView | undefined) => (view?.settings ?? {}) as Record<string, unknown>;

/**
 * Setting a key of an outside service (ADR-0048), in a dialog over the
 * integration's page. A saved key is never shown again: an empty key field
 * keeps it, so a model or a budget can change alone.
 */
export function IntegrationDialog({ kind, view, onClose }: { readonly kind: IntegrationKind; readonly view: IntegrationView | undefined; readonly onClose: () => void }): ReactNode {
  const settings = settingsOf(view);
  const configured = view?.configured === true;
  const [secret, setSecret] = useState('');
  const [login, setLogin] = useState(typeof settings['login'] === 'string' ? settings['login'] : '');
  const [owners, setOwners] = useState(Array.isArray(settings['ownerEmails']) ? (settings['ownerEmails'] as string[]).join(', ') : '');
  const [model, setModel] = useState<ContentModelId>(CONTENT_MODELS.includes(settings['model'] as ContentModelId) ? (settings['model'] as ContentModelId) : 'claude-opus-5');
  const [budget, setBudget] = useState(String(typeof settings['monthlyBudgetUsd'] === 'number' ? settings['monthlyBudgetUsd'] : 50));
  const [history, setHistory] = useState(settings['history'] !== false);

  const save = useSaveIntegration(kind, {
    onSuccess: (result) => {
      const saved = result.integrations.find((item) => item.kind === kind);
      if (saved?.checkError) toast.error('Сохранено, но проверка не прошла', saved.checkError);
      else toast.success('Сохранено и проверено');
      onClose();
    },
  });

  const readFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    void file.text().then(setSecret);
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const key = secret.trim() || undefined;
    const input: Record<string, unknown> =
      kind === 'google'
        ? { key, ownerEmails: owners.split(/[\s,;]+/).map((item) => item.trim()).filter(Boolean) }
        : kind === 'anthropic'
          ? { apiKey: key, model, monthlyBudgetUsd: Number(budget) }
          : kind === 'dataforseo'
            ? { login: login.trim(), password: secret || undefined }
            : kind === 'ahrefs'
              ? { apiKey: key, history }
              : { apiKey: key };
    save.mutate(input);
  };

  const field = errorField(save.error);
  const keyHint = configured ? 'Пусто — оставить сохранённый' : undefined;

  return (
    <Dialog
      open
      size="lg"
      title={INTEGRATION_NAMES[kind]}
      description={configured ? 'Изменение подключения' : 'Подключение'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" icon={Save} busy={save.isPending} type="submit" form="integration-form">
            Сохранить и проверить
          </Button>
        </>
      }
    >
      <form id="integration-form" className="stack" onSubmit={submit}>
        {kind === 'google' ? (
          <>
            <Field label="Ключ сервисного аккаунта (JSON)" htmlFor="google-key" hint={keyHint ?? 'Search Console API и Site Verification API должны быть включены в проекте'} {...(field === 'key' ? { error: errorText(save.error) } : {})}>
              <textarea
                id="google-key"
                className="input mono"
                rows={6}
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                placeholder={configured ? String(settings['serviceAccount'] ?? '') : '{ "type": "service_account", … }'}
                spellCheck={false}
              />
            </Field>
            <Field label="Или файл ключа" htmlFor="google-key-file">
              <input id="google-key-file" type="file" className="input" accept="application/json,.json" onChange={readFile} />
            </Field>
            <Field label="Владельцы ресурсов" htmlFor="google-owners" hint="Google-аккаунты, которые увидят сайты в своём Search Console" {...(field === 'ownerEmails' ? { error: 'Адреса Google-аккаунтов через запятую' } : {})}>
              <Input id="google-owners" value={owners} onChange={(event) => setOwners(event.target.value)} placeholder="owner@gmail.com" />
            </Field>
          </>
        ) : null}

        {kind === 'anthropic' ? (
          <>
            <Field label="API-ключ" htmlFor="anthropic-key" {...(keyHint ? { hint: keyHint } : {})} {...(field === 'apiKey' ? { error: errorText(save.error) } : {})}>
              <PasswordInput id="anthropic-key" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="off" placeholder={configured ? String(settings['keyHint'] ?? '') : 'sk-ant-…'} />
            </Field>
            <Field label="Модель">
              <Select label="Модель" value={model} options={MODEL_OPTIONS} onChange={setModel} />
            </Field>
            <Field label="Бюджет в месяц, $" htmlFor="anthropic-budget" hint="Написание статей останавливается до конца месяца" {...(field === 'monthlyBudgetUsd' ? { error: 'От 1 до 100 000' } : {})}>
              <Input id="anthropic-budget" type="number" min={1} max={100000} value={budget} onChange={(event) => setBudget(event.target.value)} />
            </Field>
          </>
        ) : null}

        {kind === 'dataforseo' ? (
          <>
            <Field label="Логин API" htmlFor="dfs-login" {...(field === 'login' ? { error: errorText(save.error) } : {})}>
              <Input id="dfs-login" value={login} onChange={(event) => setLogin(event.target.value)} autoComplete="off" />
            </Field>
            <Field label="Пароль API" htmlFor="dfs-password" {...(keyHint ? { hint: keyHint } : {})} {...(field === 'password' ? { error: errorText(save.error) } : {})}>
              <PasswordInput id="dfs-password" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="off" />
            </Field>
          </>
        ) : null}

        {kind === 'ahrefs' ? (
          <>
            <Field label="API-ключ" htmlFor="ahrefs-key" hint={keyHint ?? 'Ahrefs → Account settings → API keys. Бесплатный аккаунт тоже выдаёт ключ'} {...(field === 'apiKey' ? { error: errorText(save.error) } : {})}>
              <PasswordInput id="ahrefs-key" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="off" placeholder={configured ? String(settings['keyHint'] ?? '') : ''} />
            </Field>
            <Checkbox
              checked={history}
              onChange={setHistory}
              label="История по месяцам"
              hint="DR, ссылающиеся домены и трафик за 5 лет. Тратит единицы API: около 1 000 на домен, потом ~400 в месяц. Нужен тариф Lite или выше"
            />
          </>
        ) : null}

        {kind === 'openpagerank' ? (
          <Field label="API-ключ" htmlFor="opr-key" {...(keyHint ? { hint: keyHint } : {})} {...(field === 'apiKey' ? { error: errorText(save.error) } : {})}>
            <PasswordInput id="opr-key" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="off" placeholder={configured ? String(settings['keyHint'] ?? '') : 'opr_live_…'} />
          </Field>
        ) : null}

        {save.isError && !field ? <p className="field-error">{errorText(save.error)}</p> : null}
      </form>
    </Dialog>
  );
}
